import React, { useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { QuizRenderer } from '@/features/ai-agents/chat/QuizRenderer';
import { QuizRendererV2 } from '@/features/ai-agents/chat/QuizRendererV2';
import { dispatchChatLightboxEvent } from '@/features/ai-agents/chat/chat-lightbox-event';
import { breakpoints } from '@/components/responsive';
import {
  CodeGenerationStatus,
  CollapsibleCodeBlock,
} from '@/features/ai-agents/chat/code-generation-blocks';
import {
  CustomEventBlock,
  CustomEventLatestProvider,
  extractCustomEventKind,
} from '@/features/ai-agents/chat/custom-event-block';
import { DefaultViewHintProvider } from '@/features/ai-agents/chat/default-view-hint';
import {
  MarkdownTableBody,
  MarkdownTableCell,
  MarkdownTableHeader,
  MarkdownTableHead,
  MarkdownTableRow,
  MarkdownTableVisualization,
} from '@/features/ai-agents/chat/MarkdownTableVisualization';

interface MarkdownRendererProps {
  children?: string;
  classname?: string;
  isLatestMessage?: boolean;
  isStreamingMessage?: boolean;
}

const createComponents = (
  isLatestMessage: boolean,
  isStreamingMessage: boolean,
): Components => ({
  ...multiModalTable,
  hr() {
    return <Seperator />;
  },
  code(props) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, className, node, ...rest } = props;

    const customEventKind = extractCustomEventKind(className);
    if (customEventKind) {
      return (
        <CustomEventBlock
          kind={customEventKind}
          isLatestMessage={isLatestMessage}
        >
          {children}
        </CustomEventBlock>
      );
    }

    if (className?.includes(`vsly/json/quiz-v2`)) {
      return (
        <QuizRendererV2 isLatestMessage={isLatestMessage}>
          {children}
        </QuizRendererV2>
      );
    }

    if (className?.includes(`vsly/json/quiz`)) {
      return <QuizRenderer>{children}</QuizRenderer>;
    }
    if (className?.includes(`vsly/json/default_view`)) {
      return null;
    }

    if (className?.includes(`vsly/json/close`)) {
      return (
        <p style={{ fontFamily: `Eesti`, fontWeight: `bold` }}>
          You can close the chat while I&apos;m building the experience, this
          might take a while, and the status will update automatically once
          it&apos;s ready.
        </p>
      );
    }

    if (className?.includes(`vsly/json`)) {
      return null;
    }

    if (className?.includes(`html`)) {
      return null;
    }

    if (className?.includes(`css`)) {
      return null;
    }

    if (className?.includes(`javascript`)) {
      return null;
    }

    if (!rest.inline) {
      return (
        <CollapsibleCodeBlock
          width="70%"
          code={String(children)}
          language={className ?? ``}
          isGenerating={isLatestMessage && isStreamingMessage}
        />
      );
    }
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  img(props) {
    return (
      <Image
        src={props.src}
        alt={props.alt}
        onClick={() => props.src && dispatchChatLightboxEvent(props.src)}
      />
    );
  },
  a(props) {
    const { href } = props;

    const handler = () => {
      if (href) {
        window.postMessage(href);
      }
    };

    const executePlan = () => {
      window.dispatchEvent(
        new CustomEvent(`ai-chat-input`, {
          detail: `Go ahead with the plan`,
        }),
      );
    };

    if (
      [`#edit-visual`, `#edit-code`].includes(href) ||
      href.includes(`#edit-strategy`)
    ) {
      return (
        <>
          <br />
          <EditButton onClick={handler}>{props.children}</EditButton>
        </>
      );
    }

    if (href?.includes(`execute-plan`)) {
      return (
        <ExecutePlanButton className="execute-plan-btn" onClick={executePlan}>
          {props.children}
        </ExecutePlanButton>
      );
    }

    return (
      <a href={props.href} target="_blank" rel="noreferrer">
        {props.children}
      </a>
    );
  },
});

export function MarkdownRenderer({
  children,
  classname,
  isLatestMessage = false,
  isStreamingMessage = false,
}: MarkdownRendererProps) {
  const markdownComponents = useMemo(
    () => createComponents(isLatestMessage, isStreamingMessage),
    [isLatestMessage, isStreamingMessage],
  );

  return (
    <Wrapper>
      <DefaultViewHintProvider markdown={children ?? ``}>
        <CustomEventLatestProvider markdown={children ?? ``}>
          <ReactMarkdown
            className={classname}
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
            linkTarget="_blank"
          >
            {children}
          </ReactMarkdown>
        </CustomEventLatestProvider>
      </DefaultViewHintProvider>
      <CodeGenerationStatus
        markdown={children ?? ``}
        isLatestMessage={isLatestMessage}
        isStreamingMessage={isStreamingMessage}
      />
    </Wrapper>
  );
}

const multiModalTable: Components = {
  table({ children, node }) {
    return (
      <MarkdownTableVisualization node={node}>
        {children}
      </MarkdownTableVisualization>
    );
  },
  thead({ children }) {
    return <MarkdownTableHead>{children}</MarkdownTableHead>;
  },
  tbody({ children }) {
    return <MarkdownTableBody>{children}</MarkdownTableBody>;
  },
  tr({ children }) {
    return <MarkdownTableRow>{children}</MarkdownTableRow>;
  },
  th({ children }) {
    return <MarkdownTableHeader>{children}</MarkdownTableHeader>;
  },
  td({ children }) {
    return <MarkdownTableCell>{children}</MarkdownTableCell>;
  },
};

const Wrapper = styled.div`
  br:not(:first-child) {
    display: none;
  }
`;

const EditButton = styled.button`
  appearance: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: black;
  border: 1px solid black;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
  cursor: pointer;
  margin-top: 1rem;
  margin-right: 0.5rem;

  &:hover {
    background: black;
    color: white;
  }
`;

const Seperator = styled.div`
  width: 80%;
  margin: 4rem auto;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
`;

const ExecutePlanButton = styled.button`
  width: 20rem;
  padding: 1.2rem 2.5rem;
  border-radius: 3rem;
  background: #111111;
  color: #fff;
  font-size: 1.4rem;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s;
  margin-top: 2rem;

  border: 1.5px solid rgba(0, 0, 0, 0.2);
  font-family: Eesti, sans-serif;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const Image = styled.img`
  max-height: 15rem;
  object-fit: contain;
  object-position: top;
  cursor: zoom-in;
  border-radius: 0.4rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;
